import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/common";
import { Settings as SettingsIcon, ShieldCheck, KeyRound, Lock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ProductionVerificationPanel } from "@/components/ProductionVerificationPanel";
import { EgressConnectivityPanel } from "@/components/EgressConnectivityPanel";
import { GatewayClientCertPanel } from "@/components/GatewayClientCertPanel";

export default function Settings() {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();

  // MFA setup state
  const [mfaSetup, setMfaSetup] = useState(null);
  const [enableCode, setEnableCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Password state
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  // Recovery codes state
  const [recoveryPw, setRecoveryPw] = useState("");
  const [recoveryStep, setRecoveryStep] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [recoveryStatus, setRecoveryStatus] = useState(null);

  const loadRecoveryStatus = async () => {
    try { setRecoveryStatus((await api.get("/auth/recovery/status")).data); } catch { /* ignore */ }
  };

  // Show the remaining-code count as soon as Settings opens (never reveals the codes).
  useEffect(() => { loadRecoveryStatus(); }, []);

  const copyCodes = async () => {
    if (!newCodes) return;
    try {
      await navigator.clipboard.writeText(newCodes.join("\n"));
      toast.success("Recovery codes copied to clipboard");
    } catch { toast.error("Copy failed — select the codes and copy manually"); }
  };

  const startRecovery = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/recovery/step-up", { password: recoveryPw });
      setRecoveryStep(data.step_up_token);
      setRecoveryPw("");
      await loadRecoveryStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const generateRecovery = async (count) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/recovery/generate", { step_up_token: recoveryStep, count });
      setNewCodes(data.codes);
      toast.success(`${data.count} recovery codes generated`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revokeRecovery = async () => {
    setBusy(true);
    try {
      await api.post("/auth/recovery/revoke", { step_up_token: recoveryStep, count: 0 });
      setNewCodes(null); setRecoveryStep(""); setRecoveryStatus(null);
      toast.success("Recovery codes revoked");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startMfa = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setMfaSetup(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const enableMfa = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code: enableCode });
      toast.success("MFA enabled");
      setMfaSetup(null); setEnableCode("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disableMfa = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode });
      toast.success("MFA disabled");
      setDisableCode("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 12) return toast.error("New password must be at least 12 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Owner profile, security & preferences" icon={SettingsIcon} testId="settings-header" demo={false} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Security column: Owner Profile -> TOTP -> Recovery Codes kept together */}
        <div className="space-y-4">
        <Panel title="Owner Profile" testId="profile-panel">
          <div className="p-4 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Name</span><span className="text-slate-200">{user?.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="text-slate-200">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Role</span><span className="text-slate-200 capitalize">{user?.role}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">MFA</span>
              <span className={cn("text-xs font-mono px-2 py-0.5 rounded border", user?.mfa_enabled ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-slate-400 border-slate-600/40")}>
                {user?.mfa_enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>
        </Panel>

        <Panel title="Two-Factor Authentication (TOTP)" testId="mfa-panel">
          <div className="p-4">
            {user?.mfa_enabled ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-emerald-400 text-sm"><ShieldCheck className="w-4 h-4" /> MFA is active on your account.</div>
                <Label className="text-xs">Enter current code to disable</Label>
                <div className="flex gap-2">
                  <Input data-testid="mfa-disable-code" value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono-num" />
                  <Button data-testid="mfa-disable-btn" onClick={disableMfa} disabled={busy || disableCode.length !== 6} variant="destructive">Disable</Button>
                </div>
              </div>
            ) : !mfaSetup ? (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Protect your account with an authenticator app (Google Authenticator, Authy, 1Password).</p>
                <Button data-testid="mfa-setup-btn" onClick={startMfa} disabled={busy} className="bg-blue-600 hover:bg-blue-500 text-white">
                  <Zap className="w-4 h-4 mr-1.5" /> Set up MFA
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Scan this QR with your authenticator app, then enter the 6-digit code.</p>
                <div className="flex justify-center bg-white rounded-lg p-3 w-fit mx-auto">
                  <img src={mfaSetup.qr} alt="MFA QR" className="w-40 h-40" data-testid="mfa-qr" />
                </div>
                <div className="text-center">
                  <span className="text-[10px] text-slate-500">Manual key: </span>
                  <span className="text-[10px] font-mono text-slate-300 break-all">{mfaSetup.secret}</span>
                </div>
                <div className="flex gap-2">
                  <Input data-testid="mfa-enable-code" value={enableCode} onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono-num" />
                  <Button data-testid="mfa-enable-btn" onClick={enableMfa} disabled={busy || enableCode.length !== 6} className="bg-emerald-600 hover:bg-emerald-500 text-white">Verify & Enable</Button>
                </div>
              </div>
            )}
          </div>
        </Panel>

        <Panel title="MFA Recovery Codes" testId="recovery-panel">
          <div className="p-4 space-y-3">
            <p className="text-xs text-slate-500">Single-use backup codes to sign in if you lose your authenticator. Reverify, then generate — codes are shown only once and stored hashed.</p>
            {recoveryStatus && (
              <div className="text-[11px] text-slate-400" data-testid="recovery-status-line">
                {recoveryStatus.enabled
                  ? `${recoveryStatus.remaining} of ${recoveryStatus.total} recovery codes remaining`
                  : "No recovery codes generated yet"}
              </div>
            )}
            {!recoveryStep ? (
              <div className="flex gap-2">
                <Input data-testid="recovery-stepup-password" type="password" value={recoveryPw} onChange={(e) => setRecoveryPw(e.target.value)} placeholder="Re-enter password" className="bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
                <Button data-testid="recovery-stepup-btn" onClick={startRecovery} disabled={busy || !recoveryPw} className="bg-blue-600 hover:bg-blue-500 text-white shrink-0">Verify</Button>
              </div>
            ) : newCodes ? (
              <div className="space-y-2">
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="text-[11px] text-amber-400 font-semibold mb-2">Save these now — they will not be shown again.</div>
                  <div className="grid grid-cols-2 gap-1" data-testid="recovery-codes-list">
                    {newCodes.map((c) => <span key={c} className="font-mono-num text-sm text-slate-100 select-all">{c}</span>)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="recovery-copy-btn" onClick={copyCodes} disabled={busy} className="bg-blue-600 hover:bg-blue-500 text-white">Copy codes</Button>
                  <Button data-testid="recovery-revoke-postgen-btn" variant="destructive" onClick={revokeRecovery} disabled={busy}>Revoke all codes</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button data-testid="recovery-generate-btn" onClick={() => generateRecovery(10)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white">{recoveryStatus?.enabled ? "Regenerate 10 codes" : "Generate 10 codes"}</Button>
                {recoveryStatus?.enabled && <Button data-testid="recovery-revoke-btn" variant="destructive" onClick={revokeRecovery} disabled={busy}>Revoke all</Button>}
                {recoveryStatus && <span className="text-[11px] text-slate-500 ml-auto" data-testid="recovery-remaining-count">{recoveryStatus.remaining}/{recoveryStatus.total} unused</span>}
              </div>
            )}
          </div>
        </Panel>
        </div>

        {/* Preferences column */}
        <div className="space-y-4">
        <Panel title="Change Password" testId="password-panel">
          <div className="p-4 space-y-3">
            <div>
              <Label className="text-xs">Current Password</Label>
              <Input data-testid="current-password" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
            </div>
            <div>
              <Label className="text-xs">New Password</Label>
              <Input data-testid="new-password" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
            </div>
            <Button data-testid="change-password-btn" onClick={changePassword} disabled={busy || !pw.current_password || !pw.new_password} className="bg-blue-600 hover:bg-blue-500 text-white">
              <KeyRound className="w-4 h-4 mr-1.5" /> Update Password
            </Button>
          </div>
        </Panel>

        <Panel title="Trading Mode" testId="trading-mode-panel">
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Current Mode</span>
              <span className="text-xs font-mono px-2 py-0.5 rounded border text-amber-400 border-amber-500/30 bg-amber-500/10 uppercase">Demo</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Live Automated Trading</span>
              <span className="flex items-center gap-1 text-xs text-slate-500"><Lock className="w-3.5 h-3.5" /> Hard-disabled (Phase 1)</span>
            </div>
            <p className="text-[11px] text-slate-600 border-t border-[var(--border-subtle)] pt-3">
              Live trading cannot be activated in Phase 1. The backend enforces a permanent DEMO lock regardless of UI state.
            </p>
          </div>
        </Panel>
        </div>
      </div>
      <div className="mt-6"><ProductionVerificationPanel /></div>
      <div className="mt-6"><EgressConnectivityPanel /></div>
      <div className="mt-6"><GatewayClientCertPanel /></div>
    </div>
  );
}
